import type { Address, PublicClient } from "viem"
import { useState } from "react"
import { observer } from "mobx-react-lite"
import { useConfig, useReadContract } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm"
import { AlmProviderImageLink } from "@/domains/cl-farms-page/components/alm-provider-image-link"
import { AlertTriangleIcon, DollarIcon, TickIcon, WalletIcon } from "@/domains/icons"
import { mintFertilizerClFarmLiquidityInteraction } from "@/domains/interactions/mint-fertilizer-cl-farm-liquidity/mint-fertilizer-cl-farm-liquidity-interaction"
import { dollars } from "@/domains/library"
import { TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputSymbol } from "@/domains/tokens/components/token-input-symbol"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { INSUFFICIENT_BALANCE_ERROR } from "../../../../features/liquidity/constants"
import { ClFarmsSubHeader } from "../cl-farms-sub-header"

type PoolMessageType = "RecommendedInput" | "PriceDivergence" | "None"

export type PoolMessage = {
  message: string
  status: PoolMessageType
}

/**
 * Ichi only has one token, so we only need to consider token 0
 *
 * @param param0
 * @returns
 */
export const MintFertilizerClFarmPair = observer(({ clFarm }: { clFarm: ClFarmStore | null | undefined }) => {
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()

  const scaleApp = useScaleApplication()
  const [amountToken0, setAmountToken0] = useState<string>("0")
  const [amountToken1, setAmountToken1] = useState<string>("0")

  const mintToken0 = clFarm?.token0
  if (!mintToken0) {
    console.error("mintToken0 not found")
    return null
  }
  const amountToken0BI = clFarm?.token0.parseUnits(amountToken0) ?? 0n
  const mintValueToken0Usd = mintToken0 ? mintToken0.calculateValueUSD(Number(amountToken0)) : 0
  const hasSufficientBalanceToken0 = amountToken0BI <= mintToken0.balance

  const mintToken1 = clFarm?.token1
  if (!mintToken1) {
    console.error("mintToken1 not found")
    return null
  }

  const amountToken1BI = clFarm?.token1.parseUnits(amountToken1) ?? 0n
  const mintValueToken1Usd = mintToken1 ? mintToken1.calculateValueUSD(Number(amountToken1)) : 0
  const hasSufficientBalanceToken1 = amountToken1BI <= mintToken1.balance

  //#region tokens delta / divergence
  let poolActiveId: bigint | undefined = undefined
  const pool = clFarm.pool as Address
  const buck1 = clFarm.buck1
  try {
    const x = useReadContract({
      address: pool,
      abi: [
        {
          inputs: [],
          name: "getActiveId",
          outputs: [{ internalType: "uint24", name: "activeId", type: "uint24" }],
          stateMutability: "view",
          type: "function",
        },
      ] as const,
      functionName: "getActiveId",
      chainId: chainConfig.chainId,
    })
    poolActiveId = x.data ? (x.data as unknown as bigint) : undefined
  } catch (error: unknown) {
    console.error("error reading Fertilizer getActiveId", error)
  }

  const activeId = poolActiveId ? poolActiveId : BigInt(0)
  const delta = Number(buck1 ?? 0) - Number(activeId)

  const token0message: PoolMessage = { message: "", status: "None" }
  const token1message: PoolMessage = { message: "", status: "None" }

  // this might be the wrong way round, also we should return an object here, not a string
  if (delta > 2) {
    token0message.message = `${delta / 100}% Price Divergence!`
    token0message.status = "PriceDivergence"
    token1message.message = `Recommended Input Asset`
    token1message.status = "RecommendedInput"
  } else if (delta < -2) {
    token0message.message = `Recommended Input Asset`
    token0message.status = "RecommendedInput"
    token1message.message = `${delta / 100}% Price Divergence!`
    token1message.status = "PriceDivergence"
  }

  const canInteract =
    (amountToken0BI > 0n ? hasSufficientBalanceToken0 : true) &&
    (amountToken1BI > 0n ? hasSufficientBalanceToken1 : true) &&
    (amountToken0BI > 0n || amountToken1BI > 0n)

  const mintAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    const chainId = chainConfig.chainId
    const publicClient = scaleApp.getPublicClient()

    await mintFertilizerClFarmLiquidityInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      chainId,
      config,
      clFarm,
      publicClient,
      emitter,
      amountToken0BI,
      amountToken1BI,
      approvalMode: scaleApp?.Settings.approvalMode,

      onSuccess: () => {
        setAmountToken0("0")
        setAmountToken1("0")
        // we update our CL Farm User Info
        scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

        // the token should update it's balance
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }

  if (!clFarm) return null

  const buttonsToken0: ITokenPercentageButtons = {
    symbol: mintToken0.symbol ?? "unknown",
    imageURL: mintToken0.imageURL ?? "",
    decimals: mintToken0.decimals ?? 18,
    calculateValue: (percentage: number): string => {
      return mintToken0.calculateValuePercentage(mintToken0.balance, percentage) ?? "0"
    },
  }
  const buttonsToken1: ITokenPercentageButtons = {
    symbol: mintToken1.symbol ?? "unknown",
    imageURL: mintToken1.imageURL ?? "",
    decimals: mintToken1.decimals ?? 18,
    calculateValue: (percentage: number): string => {
      return mintToken1.calculateValuePercentage(mintToken1.balance, percentage) ?? "0"
    },
  }

  return (
    <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
      <ShowWhenTrue when={!!clFarm}>
        <ClFarmsSubHeader displayName={clFarm.displayName} tags={clFarm.tags} description={clFarm.description} />
        {/* #region Token 0 */}
        <div>
          <InnerCard hasWarning={!hasSufficientBalanceToken0}>
            <div className="flex flex-row items-center justify-between">
              <TokenInputValue
                value={amountToken0}
                token={mintToken0}
                onValueChange={(v) => setAmountToken0(v)}
                className="h-fit min-w-[180px] flex-grow p-0"
                placeholder="0"
              />
              <TokenInputSymbol token={mintToken0} className="mr-2" />
            </div>

            {/* balances */}
            <div className="flex w-full flex-row items-center justify-between text-xs">
              <ShowWhenTrue when={hasSufficientBalanceToken0}>
                <div className="flex items-center gap-1 text-sm text-t200">
                  <DollarIcon size={14} className="text-t200" />
                  <p>~{dollars(mintValueToken0Usd)}</p>
                </div>
              </ShowWhenTrue>
              <ShowWhenTrue when={!hasSufficientBalanceToken0}>
                <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
              </ShowWhenTrue>
              <Tooltip key={"balance"}>
                <TooltipTrigger asChild>
                  <TokenInputBalance token={mintToken0} label={<WalletIcon size={14} className="text-t200" />} />
                </TooltipTrigger>
                <TooltipContent>
                  <div>{mintToken0.balance.toString()}</div>
                </TooltipContent>
              </Tooltip>
            </div>

            <TokenPercentageButtons token={buttonsToken0} onClick={setAmountToken0} />
          </InnerCard>
          <PoolInputMessage poolMessage={token0message} />
        </div>
        {/* #endregion */}

        {/* #region Token 1 */}
        <div>
          <InnerCard hasWarning={!hasSufficientBalanceToken1}>
            <div className="flex flex-row items-center justify-between">
              <TokenInputValue
                value={amountToken1}
                token={mintToken1}
                onValueChange={(v) => setAmountToken1(v)}
                className="h-fit min-w-[180px] flex-grow p-0"
                placeholder="0"
              />
              <TokenInputSymbol token={mintToken1} className="mr-2" />
            </div>

            {/* balances */}
            <div className="flex w-full flex-row items-center justify-between text-xs">
              <ShowWhenTrue when={hasSufficientBalanceToken1}>
                <div className="flex items-center gap-1 text-sm text-t200">
                  <DollarIcon size={14} className="text-t200" />
                  <p>~{dollars(mintValueToken1Usd)}</p>
                </div>
              </ShowWhenTrue>
              <ShowWhenTrue when={!hasSufficientBalanceToken1}>
                <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
              </ShowWhenTrue>
              <Tooltip key={"balance"}>
                <TooltipTrigger asChild>
                  <TokenInputBalance token={mintToken1} label={<WalletIcon size={14} className="text-t200" />} />
                </TooltipTrigger>
                <TooltipContent>
                  <div>{mintToken1.balance.toString()}</div>
                </TooltipContent>
              </Tooltip>
            </div>

            <TokenPercentageButtons token={buttonsToken1} onClick={setAmountToken1} />
          </InnerCard>
          <PoolInputMessage poolMessage={token1message} />
        </div>
        {/* #endregion */}
        <div className="my-2 flex w-full items-center justify-between">
          <AlmProviderImageLink clFarm={clFarm} />
          <Button disabled={!canInteract} variant={"primary"} onClick={mintAction} size="fit">
            Mint
          </Button>
        </div>
      </ShowWhenTrue>
    </Card.Container>
  )
})

const PoolInputMessage = ({ poolMessage }: { poolMessage: PoolMessage }) => {
  if (!poolMessage) return null
  if (poolMessage.status === "None") return null

  if (poolMessage.status === "RecommendedInput") {
    return (
      <div className="flex items-center gap-1 py-2 text-xs text-green-400">
        <TickIcon size={12} />
        {poolMessage.message}
      </div>
    )
  }
  if (poolMessage.status === "PriceDivergence") {
    return (
      <div className="flex items-center gap-1 py-2 text-xs text-red-400">
        <AlertTriangleIcon size={12} />
        {poolMessage.message}
      </div>
    )
  }
}
